/**
 * Créature origami d'une SKILLCARD, dessinée en SVG à partir de sa DESCRIPTION
 * géométrique (`cards.creature`, `@jeraisonne/db/cards`). Jamais un SVG brut
 * venu de l'IA ou d'un fichier : seuls des points et des couleurs validés par
 * `cardCreatureSchema` atteignent le DOM. Les plis sont des nuances de la
 * teinte de la carte → la créature se recolore seule avec le niveau.
 *
 * Forme évoluée : plis d'évolution + halo plus fort, anneau doré qui tourne et
 * étoiles qui scintillent (animations coupées par `prefers-reduced-motion` et
 * à l'impression, cf. card-face.css).
 */

import { useId } from 'react';
import {
  CREATURE_NAMED_COLORS,
  DEFAULT_CARD_CREATURE,
  creatureSvgModel,
  parseCardCreature,
} from '@jeraisonne/db/cards';

function Sparkle({ x, y, s, delay }: { x: number; y: number; s: number; delay: number }) {
  return (
    <path
      className="jr-tool-card-creature-twinkle"
      style={{ animationDelay: `${delay}s` }}
      d={`M${x} ${y - s}Q${x} ${y} ${x + s} ${y}Q${x} ${y} ${x} ${y + s}Q${x} ${y} ${x - s} ${y}Q${x} ${y} ${x} ${y - s}Z`}
      fill={CREATURE_NAMED_COLORS.gold}
    />
  );
}

export interface OrigamiCreatureProps {
  /** Spec brute (`cards.creature`) : validée ici ; absente/invalide → créature par défaut. */
  creature: unknown;
  accent: string;
  evolved?: boolean;
  className?: string;
}

export function OrigamiCreature({
  creature,
  accent,
  evolved = false,
  className,
}: OrigamiCreatureProps) {
  const haloId = `jr-halo-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const spec = parseCardCreature(creature) ?? DEFAULT_CARD_CREATURE;
  const polys = creatureSvgModel(spec, accent, evolved);
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      role="img"
      aria-label={`Créature origami${evolved ? ' évoluée' : ''} : ${spec.species}`}
      data-evolved={evolved || undefined}
    >
      <defs>
        <radialGradient id={haloId}>
          <stop offset="0" stopColor={accent} stopOpacity={evolved ? 0.55 : 0.3} />
          <stop offset="1" stopColor={accent} stopOpacity={0} />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r={evolved ? 50 : 40} fill={`url(#${haloId})`} />
      {evolved && (
        <circle
          className="jr-tool-card-creature-spin"
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke={CREATURE_NAMED_COLORS.gold}
          strokeOpacity={0.7}
          strokeWidth={1}
          strokeDasharray="2 6"
          strokeLinecap="round"
        />
      )}
      <ellipse cx="50" cy="95" rx={evolved ? 30 : 24} ry="2.6" fill="#000" opacity={0.3} />
      <g className="jr-tool-card-creature-body">
        {polys.map((p, i) => (
          <polygon
            key={i}
            points={p.points}
            fill={p.fill}
            stroke={p.stroke}
            strokeOpacity={0.5}
            strokeWidth={0.5}
            strokeLinejoin="round"
            data-layer={p.layer}
          />
        ))}
      </g>
      {evolved && (
        <g>
          <Sparkle x={10} y={14} s={5} delay={0} />
          <Sparkle x={92} y={72} s={4} delay={0.8} />
          <Sparkle x={86} y={10} s={3.5} delay={1.5} />
          <Sparkle x={6} y={76} s={3} delay={0.4} />
        </g>
      )}
    </svg>
  );
}
